import { supabase } from './supabaseClient';

/**
 * Service for handling gig-related operations
 */

/**
 * Get all gigs with optional filtering
 * @param {object} filters - Optional filters to apply
 * @param {number} page - Page number for pagination
 * @param {number} limit - Number of gigs per page
 * @returns {Promise} - List of gigs
 */
export const getGigs = async (filters = {}, page = 1, limit = 10) => {
  try {
    const offset = (page - 1) * limit;
    
    console.log('Starting gig fetch operation with page:', page, 'limit:', limit);
    
    // Get the current user session to debug authentication issues
    const { data: sessionData } = await supabase.auth.getSession();
    console.log('Current auth session:', sessionData);
    
    console.log('Starting a fresh gigs query with no restrictions');
    
    // Basic query to get all gigs - using a simpler query first to debug
    let query = supabase
      .from('gigs')
      .select('*', { count: 'exact' })
      .order('date', { ascending: true });
      
    console.log('Fetching gigs with filters:', filters);
    
    // Apply filters if they exist
    if (filters.instrument) {
      query = query.ilike('instruments', `%${filters.instrument}%`);
    }
    
    if (filters.location) {
      query = query.ilike('location', `%${filters.location}%`);
    }
    
    if (filters.dateFrom) {
      query = query.gte('date', filters.dateFrom);
    }
    
    if (filters.dateTo) {
      query = query.lte('date', filters.dateTo);
    }
    
    if (filters.isRecurring !== undefined) {
      query = query.eq('is_recurring', filters.isRecurring);
    }
    
    if (filters.minPay) {
      query = query.gte('pay', filters.minPay);
    }
    
    // Add pagination
    query = query.range(offset, offset + limit - 1);
    
    console.log('Final query before execution:', query);
    
    const { data, error, count } = await query;
    
    console.log('Query results:', { data, error, count });
    
    if (error) {
      console.error('Error fetching gigs:', error);
      throw error;
    }
    
    // Even if no error, log empty results for debugging
    if (!data || data.length === 0) {
      console.log('No gigs found, but no error reported');
    }
    
    return { 
      gigs: data || [], 
      count, 
      page, 
      limit,
      totalPages: Math.ceil(count / limit),
      error: null 
    };
  } catch (error) {
    console.error('Error fetching gigs:', error.message);
    return { gigs: [], count: 0, page, limit, totalPages: 0, error };
  }
};

/**
 * Get a single gig by ID
 * @param {string} gigId - Gig ID
 * @returns {Promise} - Gig data
 */
export const getGigById = async (gigId) => {
  try {
    const { data, error } = await supabase
      .from('gigs')
      .select('*, profiles!gigs_created_by_fkey(full_name, venue_name, user_type, profile_image_url)')
      .eq('id', gigId)
      .single();
    
    if (error) {
      throw error;
    }
    
    return { gig: data, error: null };
  } catch (error) {
    console.error('Error fetching gig:', error.message);
    return { gig: null, error };
  }
};

/**
 * Create a new gig
 * @param {object} gigData - Gig data
 * @returns {Promise} - Created gig data
 */
export const createGig = async (gigData) => {
  try {
    // Ensure required fields are present
    if (!gigData.title || !gigData.created_by) {
      const error = new Error('Missing required fields for gig creation');
      console.error('Validation error:', error.message, 'Data:', gigData);
      throw error;
    }
    
    // Add timestamp if not provided
    if (!gigData.created_at) {
      gigData.created_at = new Date().toISOString();
    }
    
    // Set default status if not provided
    if (!gigData.status) {
      gigData.status = 'open';
    }
    
    console.log('Creating gig with data:', gigData);
    
    // Debug the Supabase client state
    console.log('Supabase client URL:', supabase.supabaseUrl);
    console.log('Supabase auth state:', await supabase.auth.getSession());
    
    // Perform the insert operation
    const insertResult = await supabase
      .from('gigs')
      .insert([gigData]);
      
    console.log('Insert result:', insertResult);
    
    if (insertResult.error) {
      console.error('Supabase insert error:', insertResult.error);
      throw insertResult.error;
    }
    
    // Fetch the inserted record
    const { data, error } = await supabase
      .from('gigs')
      .select('*')
      .eq('created_by', gigData.created_by)
      .order('created_at', { ascending: false })
      .limit(1);
    
    if (error) {
      console.error('Supabase error fetching created gig:', error);
      throw error;
    }
    
    console.log('Successfully created gig:', data);
    return { gig: data[0], error: null };
  } catch (error) {
    console.error('Error creating gig:', error.message);
    console.error('Error stack:', error.stack);
    return { gig: null, error };
  }
};

/**
 * Update an existing gig
 * @param {string} gigId - Gig ID
 * @param {object} gigData - Updated gig data
 * @returns {Promise} - Updated gig data
 */
export const updateGig = async (gigId, gigData) => {
  try {
    const { data, error } = await supabase
      .from('gigs')
      .update(gigData)
      .eq('id', gigId)
      .select();
    
    if (error) {
      throw error;
    }
    
    return { gig: data[0], error: null };
  } catch (error) {
    console.error('Error updating gig:', error.message);
    return { gig: null, error };
  }
};

/**
 * Delete a gig
 * @param {string} gigId - Gig ID
 * @returns {Promise} - Success status
 */
export const deleteGig = async (gigId) => {
  try {
    const { error } = await supabase
      .from('gigs')
      .delete()
      .eq('id', gigId);
    
    if (error) {
      throw error;
    }
    
    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting gig:', error.message);
    return { success: false, error };
  }
};

/**
 * Apply for a gig
 * @param {string} gigId - Gig ID
 * @param {string} userId - User ID
 * @param {object} applicationData - Application data
 * @returns {Promise} - Created application data
 */
export const applyForGig = async (gigId, userId, applicationData = {}) => {
  try {
    // Check if user has already applied
    const { data: existingApplications, error: checkError } = await supabase
      .from('gig_applications')
      .select('id')
      .eq('gig_id', gigId)
      .eq('applicant_id', userId);
    
    if (checkError) {
      throw checkError;
    }
    
    if (existingApplications && existingApplications.length > 0) {
      throw new Error('You have already applied for this gig');
    }
    
    // Create the application
    const { data, error } = await supabase
      .from('gig_applications')
      .insert([{
        gig_id: gigId,
        applicant_id: userId,
        status: 'pending',
        created_at: new Date().toISOString(),
        ...applicationData
      }])
      .select();
    
    if (error) {
      throw error;
    }
    
    return { application: data[0], error: null };
  } catch (error) {
    console.error('Error applying for gig:', error.message);
    return { application: null, error };
  }
};

/**
 * Get all applications for a gig
 * @param {string} gigId - Gig ID
 * @returns {Promise} - List of applications
 */
export const getGigApplications = async (gigId) => {
  try {
    const { data, error } = await supabase
      .from('gig_applications')
      .select('*, profiles!gig_applications_applicant_id_fkey(*)')
      .eq('gig_id', gigId)
      .order('created_at', { ascending: false });
    
    if (error) {
      throw error;
    }
    
    return { applications: data || [], error: null };
  } catch (error) {
    console.error('Error fetching gig applications:', error.message);
    return { applications: [], error };
  }
};

/**
 * Update application status
 * @param {string} applicationId - Application ID
 * @param {string} status - New status ('pending', 'accepted', 'rejected')
 * @returns {Promise} - Updated application data
 */
export const updateApplicationStatus = async (applicationId, status) => {
  try {
    const { data, error } = await supabase
      .from('gig_applications')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select();
    
    if (error) {
      throw error;
    }
    
    return { application: data[0], error: null };
  } catch (error) {
    console.error('Error updating application status:', error.message);
    return { application: null, error };
  }
};

/**
 * Get all gigs created by a specific user (venue)
 * @param {string} userId - User ID (venue owner)
 * @returns {Promise} - List of gigs created by the user
 */
export const getGigsByCreator = async (userId) => {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    console.log('Fetching gigs created by user:', userId);
    
    const { data, error } = await supabase
      .from('gigs')
      .select('*')
      .eq('created_by', userId)
      .order('date', { ascending: true });
    
    if (error) {
      console.error('Error fetching created gigs:', error);
      throw error;
    }
    
    console.log(`Found ${data?.length || 0} gigs created by user ${userId}`);
    
    return { 
      gigs: data || [], 
      count: data?.length || 0,
      error: null 
    };
  } catch (error) {
    console.error('Error fetching created gigs:', error.message);
    return { gigs: [], count: 0, error };
  }
};
